import React, { useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { generatePath, useParams } from 'react-router-dom';
import { ReactComponent as CopyIcon } from 'images/copy-icon.svg';
import { ReactComponent as ChevronIcon } from 'images/chevron-simple.svg';
import { ReactComponent as AssignmentIcon } from 'images/assignment-icon.svg';

import FinancialDashboardNavigation from 'components/FinancialCheckup/FinancialDashboardNavigation';
import Button, { ButtonType } from 'components/Button';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useNavigate } from 'hooks/useNavigate';
import useDropdown from 'hooks/useDropdown';
import { FlowComponentType } from 'routes/types';
import { getApplicationData } from 'selectors/getApplicationData';
import { getPaymentAccountData } from 'selectors/paymentAccount';
import { getLoanBreakdown } from 'selectors/getLoanBreakdown';
import { RoutePath } from 'enums/Routes';
import { copyToClipboard } from 'utils/copyToClipboard';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { openDirectDepositEnrollmentForm } from 'thunks';

import styles from './PaymentSettings.module.scss';

interface DataRow {
  label: string;
  value: string;
  canCopy?: boolean;
}

const PaymentSettings = ({ navigationInfo }: FlowComponentType) => {
  const navigate = useNavigate();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { loanId } = useParams<{ loanId: string }>();
  const { isOpen, handleToggleDropdown, contentRef, height } = useDropdown();
  const { application } = useSelector(getApplicationData);
  const { bankAccountData } = useSelector(getPaymentAccountData);
  const { breakdowns } = useSelector(getLoanBreakdown);
  const [isOpeningForm, setIsOpeningForm] = useState(false);

  const currentBreakdown = breakdowns.find((breakdown) => breakdown.id.toString() === loanId);

  const isACH = Boolean(currentBreakdown?.nextAutopay);

  const accountHolderName = [application?.borrowerFirstName, application?.borrowerLastName].filter(Boolean).join(' ');
  const amountPerPaycheck = formatMonetaryAmount(application?.amountPerPaycheck ?? 0, true);
  const accountNumber = bankAccountData?.accountNumber || application?.accountMask;
  const routingNumber = bankAccountData?.routingNumber;
  const bankName = bankAccountData?.bankName || application?.bankName;
  const accountType = bankAccountData?.accountType;

  const hasMoreInformationDetails = Boolean(accountHolderName) || Boolean(bankName) || Boolean(accountType);

  const handleCopy = (text: string) => {
    if (text) {
      copyToClipboard(text);
    }
  };

  const handleUpdate = () => {
    if (isACH) {
      navigate(generatePath(RoutePath.UpdateAchConnect, { loanId }) as RoutePath);
      return;
    }

    navigate(generatePath(RoutePath.UpdateEmployer, { loanId }) as RoutePath);
  };

  const handleOpenAuthorizationForm = async () => {
    if (!application?.id || isOpeningForm) return;

    setIsOpeningForm(true);
    try {
      await dispatchWithUnwrap(openDirectDepositEnrollmentForm(application.id));
    } finally {
      setIsOpeningForm(false);
    }
  };

  const directDepositRows: DataRow[] = [
    ...(accountNumber ? [{ label: 'Account number:', value: accountNumber, canCopy: true }] : []),
    ...(routingNumber ? [{ label: 'Routing number:', value: routingNumber, canCopy: true }] : []),
    {
      label: 'Per paycheck deduction:',
      value: amountPerPaycheck,
      canCopy: true,
    },
  ];

  const achRows: DataRow[] = [
    { label: 'Account holder', value: accountHolderName },
    ...(bankName ? [{ label: 'Bank', value: bankName }] : []),
    ...(accountNumber ? [{ label: 'Account number:', value: accountNumber }] : []),
    ...(routingNumber ? [{ label: 'Routing number:', value: routingNumber }] : []),
    {
      label: 'Per paycheck deduction:',
      value: amountPerPaycheck,
    },
  ];

  const rowsToRender = isACH ? achRows : directDepositRows;

  return (
    <>
      <FinancialDashboardNavigation
        {...navigationInfo}
        onBackClick={() => navigate(generatePath(RoutePath.ManageYourLoan, { loanId }) as RoutePath)}
      />
      <div className={styles.content}>
        <div className={styles.headingArea}>
          <h1 className={styles.title}>Payment settings</h1>
          <p className={styles.subtitle}>
            {isACH
              ? 'This account is used for your loan payments.'
              : 'Loan payments are made by direct deposit from your paycheck.'}
          </p>
        </div>

        <div className={styles.updateCard}>
          <div className={styles.updateTextArea}>
            <p className={styles.updateTitle}>{isACH ? 'Need to change accounts?' : 'Changed jobs?'}</p>
            {!isACH && <p className={styles.updateDescription}>Update your payments.</p>}
          </div>
          <Button type={ButtonType.Inverted} className={styles.updateButton} onClick={handleUpdate}>
            Update
          </Button>
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>{isACH ? 'Bank account' : 'Direct deposit information'}</p>
          <div className={styles.card}>
            {rowsToRender.map((row) => (
              <div key={row.label} className={styles.row}>
                <p className={styles.label}>{row.label}</p>
                <div className={styles.valueWrapper}>
                  <p className={clsx(styles.value, !row.canCopy && styles.valueNoCopy)}>{row.value}</p>
                  {row.canCopy && <CopyIcon className={styles.copyIcon} onClick={() => handleCopy(row.value)} />}
                </div>
              </div>
            ))}

            {!isACH && hasMoreInformationDetails && (
              <>
                <div className={styles.row} onClick={handleToggleDropdown}>
                  <p className={styles.moreInformation}>More information</p>
                  <ChevronIcon className={clsx(styles.chevron, isOpen && styles.chevronOpen)} />
                </div>

                <div className={styles.dropdownContent} ref={contentRef} style={{ maxHeight: `${height}px` }}>
                  {accountHolderName && (
                    <div className={styles.row}>
                      <p className={styles.label}>Employee name</p>
                      <p className={styles.value}>{accountHolderName}</p>
                    </div>
                  )}
                  {bankName && (
                    <div className={styles.row}>
                      <p className={styles.label}>Bank name</p>
                      <p className={styles.value}>{bankName}</p>
                    </div>
                  )}
                  {accountType && (
                    <div className={styles.row}>
                      <p className={styles.label}>Account type</p>
                      <p className={styles.value}>{accountType}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {!isACH && (
            <div
              role="button"
              tabIndex={0}
              className={styles.authorizationCard}
              onClick={handleOpenAuthorizationForm}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenAuthorizationForm();
                }
              }}
            >
              <div className={styles.authorizationContent}>
                <AssignmentIcon className={styles.assignmentIcon} />
                <p className={styles.authorizationLabel}>Direct Deposit Authorization form</p>
              </div>
              <ChevronIcon className={styles.authorizationChevron} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentSettings;
